import React, {Fragment, useState} from 'react';
import {Helmet} from 'react-helmet';
import {useSelector} from 'react-redux';
import b_ from 'b_';

import I18N from 'assets/i18n/brandlift-project';

import {useRumCounter} from 'client/common/utils/rum';
import {tld} from 'client/common/utils/tld';
import {openLink} from 'client/common/utils/window';
import {createUrl} from 'client/common/utils/history';
import {LinkPage} from 'client/common/utils/header-link';
import Block from 'client/common/components/block';
import {Button, Textinput} from 'client/common/components/lego';
import {Chat} from 'client/common/components/chat';
import Header, {headerMeaningfulSelector} from 'client/common/components/header2';
import Modal from 'client/common/components/modal';
import Notifications from 'client/common/components/notifications';
import {UserImage} from 'client/common/components/user/user-image';
import BlockInput from 'client/common/components/block-input';

import {useOrgsList, useCurrentOrg} from 'client/common/hooks/orgs';
import {useOrgAddRole, useOrgRoles} from 'client/common/hooks/orgs/roles';
import {useOrgUsers, useOrgRemoveUser, useOrgAddUser} from 'client/common/hooks/orgs/users';
import {getTestBuckets} from 'client/common/store/selectors/user';
import {getExperiments} from 'client/common/store/selectors/experiments';

import {RoleRow} from './role-row';

import './org-access.css';

const i18n = I18N.orgAccess;

const b = b_.with('org-access');

// eslint-disable-next-line max-statements
function OrgAccess({testBuckets}: {testBuckets: string[]}) {
    useRumCounter({
        page: 'quicksurvey.org-access',
        meaningfulPaintSelector: headerMeaningfulSelector,
        testBuckets
    });

    const [user, setUser] = useState('');
    const orgId = useCurrentOrg();

    const {isSuccess, data} = useOrgUsers(orgId!);
    const removeUser = useOrgRemoveUser(orgId!);
    const addUser = useOrgAddUser(orgId!);

    const handleClickRemoveUser = async (e: React.MouseEvent<HTMLButtonElement>) => {
        const {login} = e.currentTarget.dataset;

        await removeUser(login!);
    };

    const handleAddUserLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const login = e.target.value.trim();

        setUser(login);
    };

    const handleAddUser = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        await addUser(user);
    };

    // переключились на свои опросы со страницы доступов организации
    if (!orgId) {
        openLink(createUrl({link: LinkPage.QuicksurveyList}));
    }

    const {isSuccess: isOrgSuccess, data: orgsList} = useOrgsList();
    const {data: orgRolesResponse} = useOrgRoles(orgId);

    const addOrgRole = useOrgAddRole(orgId);

    const handleClickAddRole = async () => {
        await addOrgRole(i18n.newRoleName, []);
    };

    return (
        <div className={b()}>
            <div className={b('container')}>
                <Block
                    className={b('roles-list')}
                    header={i18n.rolesHeader}
                    description={
                        isOrgSuccess ? orgsList.orgs.find(org => org.id === orgId)?.name : ''
                    }
                >
                    <div className={b('roles-table')}>
                        <span className={b('roles-table-header', {type: 'name'})}>
                            {i18n.rolesTable.roleNameHeader}
                        </span>
                        <span className={b('roles-table-header', {type: 'count'})}>
                            {i18n.rolesTable.userCountHeader}
                        </span>
                        <span className={b('roles-table-header')}>
                            {i18n.rolesTable.roleAccessHeader}
                        </span>
                        <span className={b('roles-table-header', {last: 'yes'})} />

                        {orgRolesResponse?.roles?.map(role => {
                            return (
                                <RoleRow
                                    orgId={orgId}
                                    key={role.id}
                                    roleId={role.id}
                                    name={role.name}
                                    permissions={role.permissions}
                                    userCount={role.count}
                                    // TODO унести логику на бэк
                                    disabled={role.id === 1}
                                />
                            );
                        })}
                    </div>
                    <div className={b('roles-add')}>
                        <Button
                            className={b('add-btn')}
                            view='dl-action'
                            size='m'
                            onClick={handleClickAddRole}
                        >
                            {i18n.addRoleBtn}
                        </Button>
                    </div>
                </Block>
                <Block
                    className={b('users-add')}
                    // удалил i18n.accessHeader
                    header={i18n.usersListHeader}
                >
                    <BlockInput>
                        <h3 className={`${b('description')}`}>{i18n.addUserHeader}</h3>
                        <form className={b('add-user-row')} onSubmit={handleAddUser}>
                            <Textinput
                                size='m'
                                name='login'
                                placeholder={i18n.addUserPlaceholder}
                                view='dl'
                                onChange={handleAddUserLoginChange}
                                value={user}
                            />
                            <Button
                                className={b('add-btn')}
                                view='dl-action'
                                disabled={!user}
                                size='m'
                                type='submit'
                            >
                                {i18n.addUserBtn}
                            </Button>
                        </form>
                    </BlockInput>
                    <div className={b('users-list')}>
                        {isSuccess &&
                            data.map(usr => {
                                return (
                                    <div key={usr.email} className={b('user-row')}>
                                        <div key={usr.login} className={b('user-image')}>
                                            <UserImage
                                                email={usr.email}
                                                login={usr.login}
                                                avatar={usr.avatar}
                                                text={usr.displayName}
                                            />
                                        </div>
                                        <Button
                                            className={b('remove-btn')}
                                            view='dl-light'
                                            size='m'
                                            data-login={usr.login}
                                            onClick={handleClickRemoveUser}
                                        >
                                            {i18n.removeUserBtn}
                                        </Button>
                                    </div>
                                );
                            })}
                    </div>
                </Block>
            </div>
        </div>
    );
}

export default function OrgAccessPage() {
    const experiments = useSelector(getExperiments);
    const testBuckets = useSelector(getTestBuckets);

    return (
        <Fragment>
            <Helmet>
                <title>{i18n.pageTitle}</title>
                <meta property='og:url' content='https://surveys.yandex.ru/org/access' />
            </Helmet>
            <Header className={b('header')} />
            <OrgAccess testBuckets={testBuckets} />
            <Chat experiments={experiments} from={`org access ${tld.toUpperCase()}`} />
            <Notifications />
            <Modal />
        </Fragment>
    );
}
